/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  elasticSearch: {
    id: 'elasticSearch.elasticSearch',
    defaultMessage: 'Elasticsearch'
  },
  documents: {
    id: 'elasticSearch.documents',
    defaultMessage: 'Documents'
  },
  settings: {
    id: 'elasticSearch.settings',
    defaultMessage: 'Settings'
  },
  instance: {
    id: 'elasticSearch.instance',
    defaultMessage: 'Instance'
  },
  documentTitle: {
    id: 'elasticSearch.documentTitle',
    defaultMessage: 'File name'
  },
  noTitle: {
    id: 'elasticSearch.noTitle',
    defaultMessage: 'No file name'
  },
  options: {
    id: 'elasticSearch.options',
    defaultMessage: 'Options'
  },
  delete: {
    id: 'elasticSearch.delete',
    defaultMessage: 'Delete'
  },
  cancel: {
    id: 'elasticSearch.cancel',
    defaultMessage: 'Cancel'
  },
  dateUploaded: {
    id: 'elasticSearch.dateUploaded',
    defaultMessage: 'Upload date'
  },
  status: {
    id: 'elasticSearch.status',
    defaultMessage: 'Status'
  },
  upload: {
    id: 'elasticSearch.upload',
    defaultMessage: 'Upload'
  },
  noDocuments: {
    id: 'elasticSearch.noDocuments',
    defaultMessage: 'No documents yet'
  },
  noDocumentsDescription: {
    id: 'elasticSearch.noDocumentsDescription',
    defaultMessage: 'Upload a document in the panel on the right'
  },
  filterFilename: {
    id: 'elasticSearch.filterFilename',
    defaultMessage: 'Filter filename'
  },
  ready: {
    id: 'elasticSearch.filterFilename',
    defaultMessage: 'Ready'
  }
});

export const UploadPanelMessages = defineMessages({
  uploadDocuments: {
    id: 'elasticSearch.uploadDocuments',
    defaultMessage: 'Upload documents'
  },
  uploadSelectedFiles: {
    id: 'elasticSearch.uploadSelectedFiles',
    defaultMessage: 'Upload files'
  },
  dragDrop: {
    id: 'elasticSearch.dragDrop',
    defaultMessage: 'Drag and drop files here or click to upload'
  },
  clearFile: {
    id: 'elasticSearch.clearFile',
    defaultMessage: 'Clear file'
  },
  close: {
    id: 'elasticSearch.close',
    defaultMessage: 'Close'
  },
  maxSize: {
    id: 'elasticSearch.maxSize',
    defaultMessage:
      'Max size is 25MB per file and a limit of 20 files. Total file size max is 50MB. Supported file types are .pdf and .docx.'
  },
  uploadInProgress: {
    id: 'ElasticSearchIndexConfigMessages.uploadInProgress',
    defaultMessage: 'Sending your documents to Elasticsearch...'
  },
  tooLarge: {
    id: 'ElasticSearchIndexConfigMessages.tooLarge',
    defaultMessage: 'is too large'
  },
  isEmpty: {
    id: 'ElasticSearchIndexConfigMessages.isEmpty',
    defaultMessage: 'is empty'
  },
  totalTooLarge: {
    id: 'ElasticSearchIndexConfigMessages.totalTooLarge',
    defaultMessage: 'Total file upload size greater than 50MB'
  },
  error: {
    id: 'ElasticSearchIndexConfigMessages.error',
    defaultMessage: 'Error:'
  },
  documentsSent: {
    id: 'ElasticSearchIndexConfigMessages.documentsSent',
    defaultMessage: 'Documents sent'
  },
  processing: {
    id: 'ElasticSearchIndexConfigMessages.processing',
    defaultMessage: 'Processing has begun.'
  },
  timestamp: {
    id: 'ElasticSearchIndexConfigMessages.timestamp',
    defaultMessage: 'Timestamp:'
  }
});

export const ConversationalSearchMessages = defineMessages({
  title: {
    id: 'ConversationalSearchMessages.title',
    defaultMessage: 'Enable conversational search (optional)'
  },
  description1: {
    id: 'ConversationalSearchMessages.description1',
    defaultMessage:
      'By enabling conversational search, your assistant can generate responses from supplied documents.'
  },
  description2: {
    id: 'ConversationalSearchMessages.description2',
    defaultMessage:
      'Conversational search will use a watsonx generative AI model hosted in Dallas, TX US, to generate conversational responses. '
  },
  description2CPD: {
    id: 'ConversationalSearchMessages.description2CPD',
    defaultMessage:
      'Conversational search will use a watsonx generative AI model to generate conversational responses. '
  },
  description2Frankfurt: {
    id: 'ConversationalSearchMessages.description2Frankfurt',
    defaultMessage:
      'Conversational search will use a watsonx generative AI model hosted in Frankfurt, GER, to generate conversational responses. '
  },
  learnMore: {
    id: 'ConversationalSearchMessages.learnMore',
    defaultMessage: 'Learn more'
  },
  on: {
    id: 'ConversationalSearchMessages.on',
    defaultMessage: 'On'
  },
  off: {
    id: 'ConversationalSearchMessages.off',
    defaultMessage: 'Off'
  },
  whatDifferent: {
    id: 'ConversationalSearchMessages.whatDifferent',
    defaultMessage: "What's different?"
  },
  whatDifferentDescription: {
    id: 'ConversationalSearchMessages.whatDifferentDescription',
    defaultMessage:
      'When your assistant receives a message, the default search behavior returns short excerpts of the best search results.'
  },
  whatDifferentDescription2: {
    id: 'ConversationalSearchMessages.whatDifferentDescription2',
    defaultMessage:
      'When conversational search is enabled, your assistant will instead generate a conversational response grounded in those search results.'
  },
  convoSearchHeader: {
    id: 'ConversationalSearchMessages.convoSearchHeader',
    defaultMessage: 'Conversational search on'
  },
  defaultSearchHeader: {
    id: 'ConversationalSearchMessages.defaultSearchHeader',
    defaultMessage: 'Default search behavior (conversational search off)'
  },
  botMessage1: {
    id: 'ConversationalSearchMessages.botMessage1',
    defaultMessage: 'Welcome, how can I assist you?'
  },
  botMessage2: {
    id: 'ConversationalSearchMessages.botMessage2',
    defaultMessage:
      'I searched my knowledge base and found this information which might be useful'
  },
  botMessage3: {
    id: 'ConversationalSearchMessages.botMessage3',
    defaultMessage: 'Yes, you can use your points to book flights.'
  },
  clientMessage1: {
    id: 'ConversationalSearchMessages.clientMessage1',
    defaultMessage: 'Can I use my points on airfare?'
  },
  annotation1: {
    id: 'ConversationalSearchMessages.annotation1',
    defaultMessage:
      'Assistant’s generated responses may be slightly different every time.'
  },
  previewSearchMessage: {
    id: 'ConversationalSearchMessages.previewSearchMessage',
    defaultMessage:
      'We offer credit cards offer zero liability to our clients, protecting you from having to pay for unauthorized purchases. Can I use my points on airfare? Using points and miles to book your flights is a great option. Not only do you save money, but you can use your points to book...'
  },
  previewSearchMessageLink: {
    id: 'ConversationalSearchMessages.previewSearchMessageLink',
    defaultMessage: 'IBM watsonx Lendyr Demo Site'
  },
  pricingAndTermsInfo: {
    id: 'ConversationalSearchMessages.pricingAndTermsInfo',
    defaultMessage:
      'By using this feature you agree to the {PricingLink} and {TermsLink}. This feature is not PCI validated. The use of this feature may impact your PCI compliance.'
  }
});

export const ConfigureContentMessages = defineMessages({
  title: {
    id: 'ConfigureContentMessages.title',
    defaultMessage: 'Configure result content'
  },
  description: {
    id: 'ConfigureContentMessages.description',
    defaultMessage:
      'Specify the {name} fields you want to map to the title, body, and URL of the search response.'
  },
  fieldTitle: {
    id: 'elasticSearchConfigFields.fieldTitle',
    defaultMessage: 'Title'
  },
  fieldBody: {
    id: 'elasticSearchConfigFields.fieldBody',
    defaultMessage: 'Body'
  },
  fieldUrl: {
    id: 'elasticSearchConfigFields.fieldUrl',
    defaultMessage: 'URL (optional)'
  },
  fieldPlaceholder: {
    id: 'elasticSearchConfigFields.fieldPlaceholder',
    defaultMessage: 'Field name, ex. document.info.id'
  },
  selectOne: {
    id: 'ElasticSearchIndexConfigMessages.selectOne',
    defaultMessage: 'Choose an option'
  },
  dropdownAdvancedSettings: {
    id: 'elasticSearchConfigFields.dropdownAdvancedSettings',
    defaultMessage: 'Advanced {name} settings'
  },
  fieldFilter: {
    id: 'elasticSearchConfigFields.fieldFilter',
    defaultMessage:
      'Configure the custom result filter for Elasticsearch. This should be defined as an array of objects.'
  },
  fieldQueryBody: {
    id: 'elasticSearchConfigFields.fieldQueryBody',
    defaultMessage:
      'Configure the query body for Elasticsearch. This should be defined as an object.'
  },
  filter: {
    id: 'elasticSearchConfigFields.filter',
    defaultMessage: 'Filter'
  },
  filterPlaceholder: {
    id: 'elasticSearchConfigFields.filterPlaceholder',
    defaultMessage: 'Placeholder text'
  },
  invalidJsonArrayValue: {
    id: 'elasticSearchConfigFields.invalidArrayValue',
    defaultMessage: `Please provide a valid JSON array of objects.`
  },
  invalidJsonObjectValue: {
    id: 'elasticSearchConfigFields.invalidObjectValue',
    defaultMessage: `Please provide a valid JSON object.`
  },
  invalidEmptyValueText: {
    id: 'elasticSearchConfigFields.invalidEmptyValueText',
    defaultMessage: `Value can't be empty.`
  },
  pricingAndTermsInfo: {
    id: 'ConversationalSearchMessages.pricingAndTermsInfo',
    defaultMessage:
      'By using this feature you agree to the {PricingLink} and {TermsLink}. This feature is not PCI validated. The use of this feature may impact your PCI compliance.'
  },
  pricing: {
    id: 'ConversationalSearchMessages.pricing',
    defaultMessage: 'Pricing'
  },
  terms: {
    id: 'ConversationalSearchMessages.terms',
    defaultMessage: 'Terms'
  }
});

export const ElasticSearchIndexConfigMessages = defineMessages({
  title: {
    id: 'ElasticSearchIndexConfigMessages.title',
    defaultMessage: 'Select an index'
  },
  titleTab: {
    id: 'ElasticSearchIndexConfigMessages.titleTab',
    defaultMessage: 'Index'
  },
  description: {
    id: 'ElasticSearchIndexConfigMessages.description',
    defaultMessage:
      'Select the index you want to access from your Elasticsearch instance, or create a new index by uploading documents. '
  },
  descriptionNoUpload: {
    id: 'ElasticSearchIndexConfigMessages.descriptionNoUpload',
    defaultMessage:
      'Select the index you want to access from your Elasticsearch instance. '
  },
  learnMore: {
    id: 'ElasticSearchIndexConfigMessages.learnMore',
    defaultMessage: 'Learn more'
  },
  useIndex: {
    id: 'ElasticSearchIndexConfigMessages.useIndex',
    defaultMessage: 'Use my index'
  },
  chooseIndex: {
    id: 'ElasticSearchIndexConfigMessages.chooseIndex',
    defaultMessage: 'Choose index'
  },
  selectOne: {
    id: 'ElasticSearchIndexConfigMessages.selectOne',
    defaultMessage: 'Select one'
  },
  uploadDocs: {
    id: 'ElasticSearchIndexConfigMessages.uploadDocs',
    defaultMessage:
      'Upload documents to a new index in your Elasticsearch instance '
  },
  upgradeRequired: {
    id: 'ElasticSearchIndexConfigMessages.upgradeRequired',
    defaultMessage: 'Requirements not met'
  },
  upgradeRequiredDescription: {
    id: 'ElasticSearchIndexConfigMessages.upgradeRequiredDescription',
    defaultMessage:
      'See documentation to learn about the requirements needed for this option.'
  },
  upgradeRequiredButton: {
    id: 'ElasticSearchIndexConfigMessages.upgradeRequiredButton',
    defaultMessage: 'Go to documentation'
  },
  upgradeRequiredImage: {
    id: 'ElasticSearchIndexConfigMessages.upgradeRequiredImage',
    defaultMessage: 'Lock'
  },
  indexPlaceholder: {
    id: 'elasticInstance.indexPlaceholder',
    defaultMessage: `exampleIndex`
  },
  index: {
    id: 'elasticInstance.indexPlaceholder',
    defaultMessage: `Elasticsearch index`
  },
  invalidEmptyValueText: {
    id: 'elasticSearchConfigFields.invalidEmptyValueText',
    defaultMessage: `Value can't be empty.`
  }
});
